import React, { memo, useMemo } from 'react';

import size from '@/assets/theme/size';
import Image from '@/components/atomic/atoms/layout/image/image';
import Link from '@/components/atomic/atoms/navigation/link';
import type {
	BrandHeights,
	LogoProps,
} from '@/components/atomic/molecules/navigation/logo/typings';
import withExposer from '@/hocs/with-exposer';
import useActiveBrand from '@/hooks/use-active-brand';
import useDynamicLogoHeightEnabled from '@/hooks/use-dynamic-logo-height-enabled/use-dynamic-logo-height-enabled.ts';
import { BasicComponentProps } from '@/typings/generic';
import normalisePath from '@/utils/path-helper';

const { desktopSm } = size;

const LogoComponent: React.FC<LogoProps & BasicComponentProps> = ({
	logoImageAlt,
	logoImg,
	logoLink,
	fallbackLogo,
	$exposerSuffix,
}) => {
	const activeBrand = useActiveBrand();
	const dynamicLogoHeightEnabled = useDynamicLogoHeightEnabled();

	const $height = useMemo(() => {
		const brandHeights: BrandHeights = dynamicLogoHeightEnabled
			? {
					defender: 12,
				}
			: {};

		let brandHeight: number | undefined;
		if (activeBrand && brandHeights[activeBrand]) {
			brandHeight = brandHeights[activeBrand];
		}

		return fallbackLogo ? 31 : (brandHeight ?? 8);
	}, [fallbackLogo, activeBrand]);

	const $breakpoints = useMemo(() => {
		const brandHeightsDesktopSm: BrandHeights = dynamicLogoHeightEnabled
			? {
					defender: 15,
				}
			: {};

		let brandHeight: number | undefined;
		if (activeBrand && brandHeightsDesktopSm[activeBrand]) {
			brandHeight = brandHeightsDesktopSm[activeBrand];
		}

		return {
			[desktopSm]: {
				$height: fallbackLogo ? 45 : (brandHeight ?? 10),
			},
		};
	}, [fallbackLogo]);

	if (!logoImg) return null;

	return (
		<Link
			href={normalisePath(logoLink)}
			title={logoImageAlt}
			$alignItems='center'
			$exposerSuffix={$exposerSuffix}
			$breakpoints={{
				[desktopSm]: {
					$padding: '0 40px',
				},
			}}
		>
			<Image
				src={logoImg}
				alt={logoImageAlt}
				$height={$height}
				$breakpoints={$breakpoints}
				$exposerSuffix={$exposerSuffix}
			/>
		</Link>
	);
};

const Logo = memo(withExposer(LogoComponent, 'logo'));

export default Logo;
